/**
 * The financial-tools drawing catalogue — 26 annotations in five groups.
 *
 * `scichart-financial-tools` ships the annotations and a
 * `MultiPointAnnotationPlacementModifier` that places them click-by-click. Its
 * own documentation calls `startPlacement` "the cleanest approach for
 * toolbar-driven apps", which is exactly this: one modifier per surface, and
 * the toolbar names a type when a tool is picked.
 *
 * So this file is the *menu*, not the drawing. It holds no SciChart import and
 * needs no browser, which is the point — the grouping and the labels are the
 * part worth testing, and everything that needs WebGL is behind the builder.
 *
 * ## Grouped by what you are drawing, not by class name
 *
 * Twenty-six icons in a 34px column is not a palette, it is a list — and so is
 * nine, once the four built-in groups and the clear button are in the same half
 * of the strip. **Five**, named for what you are drawing rather than for the
 * library's classes.
 *
 * Merged where the names already say the same thing: channels and pitchforks
 * are both parallel structure drawn off a swing, and Fibonacci and cycles are
 * both a ratio measured along an axis — price for one, time for the other. Two
 * icons for one gesture is two places to look for it.
 *
 * A popout of eight is fine where a column of eight is not: it opens sideways
 * across the chart, where there is room.
 *
 * The one-icon-per-group rule comes from the existing `AnnotationTools`, which
 * already groups the built-in kinds this way. These extend that strip rather
 * than replacing it — though the reason they are still distinguished is now
 * only which modifier places them: since adoption, both sources persist into
 * `Pane.annotations` alike.
 *
 * ## The type strings are the contract with the library
 *
 * `ETradingAnnotationType` is a string enum, so the values here are exactly the
 * ones `startPlacement` takes. A test asserts every one of them exists in the
 * installed package — a typo would otherwise be a tool that silently does
 * nothing, which is the failure mode this whole palette is most prone to.
 */

/**
 * One drawing tool, from either source.
 *
 * **The two sources sit side by side in one menu and behave differently**, and
 * that is the point of the discriminator rather than something it papers over:
 *
 * - `builtin` is a contract `Annotation`. It is placed by our own
 *   `DrawingModifier`, shredded into `Pane.annotations` by qv-data, and it is
 *   **still there after a reload**.
 * - `trading` is a `scichart-financial-tools` annotation. It is placed by the
 *   library's `MultiPointAnnotationPlacementModifier` and **then adopted**: the
 *   builder reads the finished drawing's anchors, mints an id and reports it
 *   like any other, so it lands in `Pane.annotations` too.
 *
 * So the discriminator now says only *who places it*, not what survives. Both
 * persist, both can be selected, restyled and deleted. It was not always so —
 * the contract had seven kinds against this palette's twenty-six, and a
 * pitchfork had nowhere to be stored.
 *
 * Grouped by what you are drawing rather than by which library draws it,
 * because that difference is ours and not the user's.
 */
export type TradingTool =
  | { kind: "builtin"; tool: string; label: string; glyph: string }
  | { kind: "trading"; type: string; label: string; glyph: string }
  // The trade-plan gesture. Neither library draws it: the drag defines an entry
  // and a stop, and what lands on the chart is a **journal plan**, rendered
  // from the journal like any other. The only tool here whose result outlives
  // the chart in a table rather than a workspace.
  | { kind: "plan"; label: string; glyph: string };

export interface TradingToolGroup {
  name: string;
  glyph: string;
  tools: TradingTool[];
}

/** A contract annotation kind — persists. */
const builtin = (tool: string, label: string, glyph: string): TradingTool => ({
  kind: "builtin", tool, label, glyph,
});

/** A financial-tools annotation — richer, and not persisted yet. */
const trading = (type: string, label: string, glyph: string): TradingTool => ({
  kind: "trading", type, label, glyph,
});

/** The trade-plan gesture — becomes a row in the journal. */
const planTool = (label: string, glyph: string): TradingTool => ({ kind: "plan", label, glyph });

/**
 * The six groups, in the order the strip shows them.
 *
 * Lines first because they are what most drawings are; Draw last because
 * reaching for freehand is a deliberate act rather than a reflex.
 */
export const TRADING_TOOL_GROUPS: TradingToolGroup[] = [
  {
    name: "Lines",
    glyph: "╱",
    tools: [
      builtin("trendline", "Trend line", "╱"),
      trading("ExtendedLineAnnotation", "Extended line", "↗"),
      builtin("line", "Line segment", "─"),
      // **Two horizontals, and the difference is what gets stored.** Ours keeps
      // a price and nothing else — no x, so there is no anchor to drift and the
      // drawing is exactly the number you are watching. The library's keeps a
      // point whose x it then ignores, since `extendStart` and `extendEnd` both
      // default true and it spans the pane just the same.
      //
      // Named for what you reach for it for, rather than for its shape, because
      // "Horizontal line" describes both of them equally well.
      builtin("hline", "Price level", "┅"),
      trading("HorizontalTrendLineAnnotation", "Horizontal line", "━"),
      trading("VerticalTrendLineAnnotation", "Vertical line", "┃"),
      trading("CrossLineAnnotation", "Cross line", "✛"),
      trading("AngleLineAnnotation", "Angle line", "∠"),
      trading("PolyLineAnnotation", "Polyline", "⌇"),
    ],
  },
  {
    name: "Shapes",
    glyph: "▭",
    tools: [
      builtin("box", "Rectangle", "▭"),
      trading("SectorAnnotation", "Sector", "◔"),
      // One click marks one bar; clicking it again takes it off.
      builtin("barHighlight", "Highlight bar", "▮"),
    ],
  },
  {
    name: "Channels & Pitchforks",
    glyph: "⫽",
    tools: [
      trading("ChannelAnnotation", "Parallel channel", "⫽"),
      trading("FlatBottomChannelAnnotation", "Flat-bottom channel", "⊿"),
      trading("DisjointChannelAnnotation", "Disjoint channel", "⋰"),
      trading("PitchforkAnnotation", "Andrews pitchfork", "⋔"),
      trading("SchiffPitchforkAnnotation", "Schiff pitchfork", "⋎"),
      trading("ModifiedSchiffPitchforkAnnotation", "Modified Schiff", "⋏"),
      trading("InsidePitchforkAnnotation", "Inside pitchfork", "⋕"),
      trading("PitchfanAnnotation", "Pitchfan", "≣"),
    ],
  },
  {
    name: "Fibonacci & Cycles",
    glyph: "≡",
    tools: [
      trading("FibonacciRetracementAnnotation", "Retracement", "≡"),
      trading("FibonacciExtensionAnnotation", "Extension", "⋮"),
      trading("FibonacciCirclesAnnotation", "Circles", "◎"),
      trading("FibonacciSpeedResistanceArcsAnnotation", "Speed resistance arcs", "◜"),
      trading("FibonacciWedgeAnnotation", "Wedge", "◁"),
      trading("FibonacciTimeZoneAnnotation", "Time zones", "⁞"),
      trading("CyclicLineAnnotation", "Cyclic lines", "∥"),
      trading("CyclicArcAnnotation", "Cyclic arcs", "∿"),
    ],
  },
  {
    name: "Trade",
    glyph: "⇅",
    tools: [
      // First, because it is the one that means something afterwards: drag from
      // the entry to the stop and a plan exists in the journal, sized against
      // the account, with its levels drawn from it.
      planTool("Trade plan", "◪"),
      trading("StopLossTakeProfitAnnotation", "Stop loss / take profit", "⇅"),
      trading("MeasureAnnotation", "Measure", "⤢"),
    ],
  },
  {
    name: "Draw",
    glyph: "✎",
    tools: [
      // **Ours, not the library's.** Both exist and draw the same gesture, and
      // the difference is everything that happens afterwards: a contract
      // `freehand` is stored in `Pane.annotations`, comes back on a reload, and
      // can be selected and restyled. The library's is a picture on a surface.
      // It was armed here only because nothing else offered the tool.
      builtin("freehand", "Freehand", "✎"),
      builtin("text", "Text", "T"),
    ],
  },
];

/** Every tool, flattened — for lookups and for the test that pins the catalogue. */
export const TRADING_TOOLS: TradingTool[] = TRADING_TOOL_GROUPS.flatMap((g) => g.tools);

/** Every financial-tools type the palette offers. */
export const TRADING_TYPES: string[] = TRADING_TOOLS.filter(
  (t): t is Extract<TradingTool, { kind: "trading" }> => t.kind === "trading",
).map((t) => t.type);
